import { doc, onSnapshot } from 'firebase/firestore';
import get from 'lodash/get';
import { useCallback, useEffect, useRef } from 'react';
import type { UnknownAction } from 'redux';
import { useDispatch } from 'react-redux';
import type { ThunkDispatch } from 'redux-thunk';
import { useFirebase } from 'containers/FirebaseContext';
import { useSocket } from 'store/chat/selectors';
import { handleMessageChat } from 'store/chat/thunks';
import { usePlayModal } from 'store/one_game/selectors';

type AppThunkDispatch = ThunkDispatch<Record<string, unknown>, undefined, UnknownAction>;

type SocketUser = {
    id?: string;
    role?: string;
    [key: string]: unknown;
};

type SocketClass = {
    id?: string;
    [key: string]: unknown;
};

type WrapperChatSocketProps = {
    currentUser: SocketUser | null | undefined;
    currentClass: SocketClass | null | undefined;
};

type MessageLike = { data: unknown };

const isMessageLike = (value: unknown): value is MessageLike =>
    typeof value === 'object' && value !== null && 'data' in value;

const WrapperChatSocket = ({ currentUser, currentClass }: WrapperChatSocketProps) => {
    const dispatch = useDispatch<AppThunkDispatch>();
    const socketRef = useRef<WebSocket | null>(null);
    const { socketConnection, reCreateSocket } = useSocket();
    const { firestore } = useFirebase();
    const { playGameModal } = usePlayModal();

    const onMessage = useCallback(
        (event: MessageEvent | MessageLike) => {
            const raw = isMessageLike(event) ? event.data : (event as MessageEvent).data;
            dispatch(handleMessageChat(raw, get(currentUser, 'id'), currentUser, currentClass));
        },
        [dispatch, currentUser, currentClass],
    );

    const onclose = useCallback(
        (event: CloseEvent) => {
            if (currentUser && currentUser.id && event.reason !== 'logout_close') {
                reCreateSocket();
            }
        },
        [currentUser, reCreateSocket],
    );

    const onOpen = useCallback(() => {}, []);
    const onError = useCallback(() => {}, []);

    const removeSocketListner = useCallback(() => {
        if (socketRef.current) {
            socketRef.current.onmessage = null;
            socketRef.current.onclose = null;
            socketRef.current.onopen = null;
            socketRef.current.onerror = null;
        }
    }, []);

    const socketListner = useCallback(() => {
        removeSocketListner();
        if (!socketRef.current) return;
        socketRef.current.onmessage = onMessage as unknown as (this: WebSocket, ev: MessageEvent) => void;
        socketRef.current.onclose = onclose;
        socketRef.current.onopen = onOpen;
        socketRef.current.onerror = onError;
    }, [removeSocketListner, onMessage, onclose, onOpen, onError]);

    useEffect(() => {
        if (socketConnection) {
            socketRef.current = socketConnection;
            socketListner();
        } else {
            removeSocketListner();
        }
    }, [socketConnection, currentClass, currentUser, socketListner, removeSocketListner]);

    useEffect(() => {
        let unsubscribe: (() => void) | null = null;
        if (playGameModal.open && playGameModal.data?.session_id) {
            const sessionRef = doc(firestore, 'sessions', playGameModal.data.session_id);

            unsubscribe = onSnapshot(
                sessionRef,
                (docSnap) => {
                    if (docSnap.exists()) {
                        const sessionFields = docSnap.data() as Record<string, unknown>;
                        const gameData: Record<string, unknown> = { id: docSnap.id, ...sessionFields };
                        if (gameData.game_status === 'ended' && gameData.ended_reason === 'teacher_inactive') {
                            onMessage({
                                data: {
                                    code: 'stop_game_notification',
                                    userId: currentUser?.id,
                                    classId: currentClass?.id,
                                    game_data: {},
                                    session_id: playGameModal.data?.session_id,
                                    session_code: playGameModal.data?.session_code,
                                    game_status: 'ended',
                                },
                            });
                        }
                    }
                },
                (error) => {
                    console.error('Error listening to game status:', error);
                },
            );
        }

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [
        playGameModal.open,
        playGameModal.data?.session_id,
        playGameModal.data?.session_code,
        firestore,
        currentUser?.id,
        currentClass?.id,
        onMessage,
    ]);

    const removeSocketListnerRef = useRef(removeSocketListner);
    removeSocketListnerRef.current = removeSocketListner;

    useEffect(() => {
        return () => {
            removeSocketListnerRef.current();
            socketRef.current = null;
        };
    }, []);

    return null;
};

export default WrapperChatSocket;
